import { useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
// import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import useApi from 'hooks/useApi';

// project import
// import Avatar from 'components/@extended/Avatar';
import { useGetMenuMaster } from 'api/menu';
import NotificationSettings from 'components/NotificationSettings';

// assets
import { ArrowRight2, NotificationBing, Logout } from 'iconsax-react';

// import avatar1 from 'assets/images/users/avatar-6.png';

interface ExpandMoreProps extends IconButtonProps {
  theme: Theme;
  expand: boolean;
  drawerOpen: boolean;
}

const ExpandMore = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'theme' && prop !== 'expand' && prop !== 'drawerOpen' })(
  ({ theme, expand, drawerOpen }: ExpandMoreProps) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(-90deg)',
    marginLeft: 'auto',
    color: theme.palette.secondary.dark,
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest
    }),
    ...(!drawerOpen && {
      opacity: 0,
      width: 50,
      height: 50
    })
  })
);

// ==============================|| LIST - USER ||============================== //

export default function NavUser() {
  const theme = useTheme();
  const navigate = useNavigate();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const { logout, user } = useApi();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationSettings = () => {
    setNotificationDialogOpen(true);
    handleClose();
  };

  const handleNotificationDialogClose = () => {
    setNotificationDialogOpen(false);
  };

  // Get user name with fallback
  const userName = user?.firstName + (user?.lastName ? ` ${user?.lastName}` : '') || 'Guest User';
  
  // Handle both traditional role objects and resource type strings
  const userRole = (() => {
    if (!user?.role) return 'User Role Not Set';

    // If role is a string (resource type), use it directly
    if (user.role?.name) {
      return user.role.name;
    }
    
    // If role is an object with roleName, use that
    if (user.role?.roleName) {
      return user.role.roleName;
    }
    
    return 'User Role Not Set';
  })();

  return (
    <Box sx={{ p: 1.25, px: !drawerOpen ? 1.25 : 3, borderTop: '2px solid ', borderTopColor: 'divider' }}>
      <List disablePadding>
        <ListItem
          disablePadding
          secondaryAction={
            <ExpandMore
              theme={theme}
              expand={open}
              drawerOpen={drawerOpen}
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              aria-label="show more"
            >
              <ArrowRight2 style={{ fontSize: '0.625rem' }} />
            </ExpandMore>
          }
          sx={{
            ...(!drawerOpen && { display: 'flex', justifyContent: 'flex-end' }),
            '& .MuiListItemSecondaryAction-root': { right: !drawerOpen ? 16 : -16 }
          }}
        >
          <ListItemText 
            primaryTypographyProps={{ sx: { color: '#BEC8D0' } }} 
            primary={userName} 
            sx={{ ...(!drawerOpen && { display: 'none' }) }} 
            secondary={userRole} 
          />
        </ListItem>
      </List>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem onClick={handleLogout}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logout size={18} />
            Logout
          </Box>
        </MenuItem>
        <MenuItem onClick={handleNotificationSettings}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationBing size={18} />
            Notifications
          </Box>
        </MenuItem>
      </Menu>

      {/* Notification Settings Dialog */}
      <Dialog
        open={notificationDialogOpen}
        onClose={handleNotificationDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <NotificationBing size={20} />
          </Box>
          <Box>
            Notification Settings
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          <NotificationSettings />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleNotificationDialogClose} variant="outlined" sx={{color:"text.dark", borderColor:"secondary.dark"}}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
